import Popper from '@mui/material/Popper';
import { format, parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cx from 'classnames';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { getTimeValue } from '../components/AppointmentDatePicker';
import Button from '../components/Button';
import Invoice from '../components/Invoice';
import useAuthentication from '../hooks/useAuthentication';
import { ReactComponent as Calendar } from '../images/calendar.svg';
import { ReactComponent as Clock } from '../images/clock.svg';
import { ReactComponent as Location } from '../images/location.svg';
import styles from '../sass/components/Success.module.scss';
import {
  ROUTE_ACCOUNT_ROOT,
  ROUTE_BOOK_PAYMENT,
  ROUTE_ROOT,
} from '../util/constants';
import {
  selectPotentialFranchiseId,
  setPotentialFranchiseId,
  selectVanId,
  setStoredDiscountAmount,
  setStoredDiscountCodeId,
  setStoredDiscountType,
  setStoredPromoCreditAmount,
  selectPaymentPreference,
  selectPrepayDiscountPercent,
  selectPrepaymentState,
  selectPrepaidAmountCents,
  selectPrepaymentToken,
  selectCreatedWorkOrderId,
  selectExistingAccount,
  selectSlotPrice,
  selectPayerRequest,
  selectPayerSelected,
  resetBookingPricing,
} from '../features/booking/bookingSlice';
import PayerPaymentNote from '../components/PayerPaymentNote';
import { mp } from '../util/analytics';
import {
  aprPurchase,
  bookingCurrency,
  planForBasket,
} from '../util/aperioTracking';
import { aperioPlanKind } from '../util/aperioPlanCopy';
import { selectServicesTotal } from '../features/serviceTypes/serviceTypesSlice';
import { selectAddOnTotal } from '../features/addOns/addOnsSlice';
import {
  formatCents,
  PaymentPreference,
  prepayDiscountCents,
} from '../util/bookingPricing';
import CreateAccountPrompt, {
  ExistingAccountPrompt,
} from '../components/CreateAccountPrompt';
import { useTenant } from '../components/TenantContext';
import { presetBrand } from '../util/presetBrand';
import { normalizeThemePreset } from '../util/themePreset';
import AperioConfirmation from '../components/AperioConfirmation';
import { ConsumerConfirmation } from '../consumer/SkinRoutes';
import { useSkin } from '../consumer/skin';
import AddToCalendar from '../components/AddToCalendar';
import useBookingCalendarLinks from '../hooks/useBookingCalendarLinks';
import useBookingWalletLink from '../hooks/useBookingWalletLink';
import { formatDate } from '../i18n/format';
import { portalLanguage } from '../i18n';
import { PageMain } from '../components/SkipLink';
import {
  MARKETING_LANGUAGES,
  marketingBase,
  marketingFooterCopy,
} from '../util/marketingSite';

export default function Success() {
  const dispatch = useAppDispatch();
  // A preset tenant (Aperio) names itself and its own inbox here; every
  // other tenant, Go Oil included, keeps the Go Oil wording and addresses.
  const tenant = useTenant();
  const brand = presetBrand(tenant?.themePreset);
  // Aperio renders its own page (components/AperioConfirmation.tsx); every
  // other tenant keeps this one exactly as it was.
  const aperio = normalizeThemePreset(tenant?.themePreset) === 'aperio';
  const skin = useSkin();
  const brandName = brand?.name ?? 'Go Oil';
  const helloEmail = brand?.email ?? 'hello@gooil.ca';
  const supportEmail = brand?.email ?? 'support@gooil.ca';
  const [anchor, setAnchor] = useState<null | HTMLDivElement>(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [currentImageSrc, setCurrentImageSrc] = useState('');
  const { loggedIn } = useAuthentication();
  const anchorEl = useRef<null | HTMLDivElement>(null);
  const name = useAppSelector((state) => state.serviceLocation.contact.name);
  const contactEmail = useAppSelector(
    (state) => state.serviceLocation.contact.email,
  );
  const contactPhone = useAppSelector(
    (state) => state.serviceLocation.contact.phoneNumber,
  );
  const createdWorkOrderId = useAppSelector(selectCreatedWorkOrderId);
  // The booking this page is about, fixed once known: the number shown and
  // the purchase conversion always name the same work order (QA 2026-09-25
  // #12: the page showed #53493 while the conversion said wo_53494).
  const [workOrderId, setWorkOrderId] = useState<number | null>(
    createdWorkOrderId,
  );
  useEffect(() => {
    if (workOrderId == null && createdWorkOrderId != null) {
      setWorkOrderId(createdWorkOrderId);
    }
  }, [workOrderId, createdWorkOrderId]);
  // The email already had an account and the booking is on it: true offers
  // a login instead of "set a password"; false (another brand's account,
  // which cannot log in here) offers neither.
  const existingAccount = useAppSelector(selectExistingAccount);
  const [loggedInHere, setLoggedInHere] = useState(false);
  const car = useAppSelector((state) => state.car);
  const dateTime = useAppSelector((state) => state.serviceLocation.dateTime);

  const serviceAddress = useAppSelector(
    (state) => state.serviceLocation.serviceAddress,
  );
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const customer = useAppSelector((state) => state.customer);
  const navigate = useNavigate();
  const potentialFranchiseId = useAppSelector(selectPotentialFranchiseId);
  const potentialVanId = useAppSelector(selectVanId);
  const paymentPreference = useAppSelector(selectPaymentPreference);
  const prepayDiscountPercent = useAppSelector(selectPrepayDiscountPercent);
  const slotPrice = useAppSelector(selectSlotPrice);
  const prepaymentState = useAppSelector(selectPrepaymentState);
  const prepaidAmountCents = useAppSelector(selectPrepaidAmountCents);
  const prepaymentToken = useAppSelector(selectPrepaymentToken);
  // "Send the bill to your partner" (Aperio): where the pay link went.
  const payerSelected = useAppSelector(selectPayerSelected);
  const payerRequest = useAppSelector(selectPayerRequest);
  // A booking nobody serves is never prepaid (the server books it as
  // pay-at-visit and hands out no pay token): no "Pay now", no prepay copy.
  const isPrepay = paymentPreference === PaymentPreference.prepay
    && !!potentialFranchiseId;

  useEffect(() => {
    setAnchor(anchorEl.current);
  }, []);

  useEffect(() => {
    // Aperio's page has no map (nor the pop-up anchored to it).
    if (aperio) return;
    const loadingImage = new Image();
    loadingImage.src = 'https://maps.googleapis.com/maps/api/staticmap'
      + '?size=600x600&scale=1&format=png&maptype=roadmap'
      + '&markers=size:normal'
      + '%7Ccolor:red%7C'
      + `${serviceAddress?.lat},`
      + `${serviceAddress?.lng}`
      + `&key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
    loadingImage.onload = () => {
      setMapLoading(false);
      setCurrentImageSrc(loadingImage.src);
    };
  });

  const open = Boolean(anchor);
  const appointmentDate = dateTime
    ? format(parseISO(dateTime.date), 'MMMM dd, yyyy')
    : null;
  const appointmentTime = dateTime ? getTimeValue(dateTime) : '';

  const smallAppointmentDate = dateTime
    ? format(parseISO(dateTime.date), 'MM/dd/yyyy')
    : null;
  const smallAppointmentTime = dateTime
    ? format(parseISO(dateTime.date), 'HH:mm')
    : null;

  const isFranchiseRole = currentUser.roles?.some(
    (role: string) => [
      'manager',
      'technician',
      'tester',
      'sales',
    ].includes(role),
  );
  // "Add to calendar" for the customer once the time is confirmed (a van
  // is on it); staff booking for someone else do not get it.
  const calendarLinks = useBookingCalendarLinks(
    workOrderId,
    contactEmail,
    !!potentialVanId && !!potentialFranchiseId && !isFranchiseRole,
  );
  // "Add to Google Wallet" (Aperio only): the server answers only for a
  // booking that carries a plan, while the wallet is configured.
  const walletSaveUrl = useBookingWalletLink(
    workOrderId,
    contactEmail,
    aperio && !isFranchiseRole,
  );
  const serviceTotal = useAppSelector((
    state,
  ) => selectServicesTotal(state));

  const addOnTotal = useAppSelector((state) => selectAddOnTotal(state));

  const selectedServices = (useAppSelector(
    (state) => state.serviceTypes.services,
  ) || []);

  const selectedAddOns = (useAppSelector((state) => state.addOns.addOns) || []);

  const hasTracked = useRef(false);

  useEffect(() => {
    if (hasTracked.current) return;
    if (selectedServices.length === 0 && selectedAddOns.length === 0) return;
    // Aperio: `Booking Completed` and `purchase` go out together, so wait for
    // the booking's work order (the purchase's transaction id) instead of
    // spending this one chance before it is known - which used to send
    // Booking Completed with no purchase at all. Every other tenant is
    // tracked exactly as before.
    if (aperio && workOrderId == null) return;

    const preTaxCents = Math.round((serviceTotal + addOnTotal) * 100)
      + (slotPrice?.cents ?? 0);
    const prepayCents = isPrepay
      ? prepayDiscountCents(preTaxCents, prepayDiscountPercent)
      : 0;
    mp.track('Booking Completed', {
      // Joins this event to the server's `Service Completed` for the visit.
      work_order_id: workOrderId ?? undefined,
      revenue_cents: preTaxCents + prepayCents,
      slot_price_adjustment_cents: slotPrice?.cents ?? 0,
      slot_price_tier: slotPrice?.tier ?? null,
      payment_preference: paymentPreference,
      prepay_discount_cents: prepayCents,
    });

    // Aperio contract `purchase` (no-op for every other tenant): the
    // amount charged for a prepaid booking, else the booking's pre-tax
    // total as above. transaction_id `wo_<id>` is the server's CAPI event id.
    if (workOrderId) {
      const prepaid = prepaymentState === 'PAID' && !!prepaidAmountCents;
      aprPurchase({
        workOrderId,
        valueCents: prepaid ? prepaidAmountCents : preTaxCents + prepayCents,
        currency: bookingCurrency(serviceAddress?.countryCode),
        plan: planForBasket(selectedServices.map((service) => service.nameKey)),
        payment: prepaid ? 'prepaid' : 'at_visit',
        email: contactEmail,
        phone: contactPhone,
        // What was booked, for GA4 `items`: names and prices, nothing else.
        items: [
          ...selectedServices.map((service) => ({
            id: String(service.nameKey || service.id),
            name: String(service.name),
            priceCents: Math.round((Number(service.value) || 0) * 100),
            category: aperioPlanKind(service.nameKey) ? 'plan' as const : 'service' as const,
          })),
          ...selectedAddOns.map((addOn) => ({
            id: String(addOn.id),
            name: String(addOn.name),
            priceCents: Math.round((Number(addOn.value) || 0) * 100),
            category: 'add_on' as const,
          })),
        ],
      }, serviceAddress?.countryCode);
    }

    selectedAddOns.forEach((addOn) => {
      mp.track('Add-on Added', {
        add_on_name: addOn.name,
        price_cents: Math.round(addOn.value * 100),
      });
    });

    selectedServices.forEach((service) => {
      mp.track('Service Selected', {
        service_name: service.name, // ← fixed
        price_cents: Math.round(service.value * 100),
      });
    });

    hasTracked.current = true;
  }, [
    serviceTotal,
    addOnTotal,
    selectedServices,
    selectedAddOns,
    slotPrice,
    isPrepay,
    paymentPreference,
    prepayDiscountPercent,
    workOrderId,
    prepaymentState,
    prepaidAmountCents,
    serviceAddress?.countryCode,
    contactEmail,
    contactPhone,
    aperio,
  ]);

  const newAppointment = () => {
    dispatch(setPotentialFranchiseId(null));
    dispatch(setStoredDiscountAmount(null));
    dispatch(setStoredDiscountCodeId(null));
    dispatch(setStoredDiscountType(null));
    dispatch(setStoredPromoCreditAmount(null));
    dispatch(resetBookingPricing());
    navigate(ROUTE_ROOT);
    window.location.reload();
  };

  const goHome = () => {
    dispatch(setPotentialFranchiseId(null));
    dispatch(setStoredDiscountAmount(null));
    dispatch(setStoredDiscountCodeId(null));
    dispatch(setStoredDiscountType(null));
    dispatch(setStoredPromoCreditAmount(null));
    dispatch(resetBookingPricing());
    navigate(ROUTE_ROOT);
  };

  const goToAccount = () => {
    dispatch(setPotentialFranchiseId(null));
    dispatch(setStoredDiscountAmount(null));
    dispatch(setStoredDiscountCodeId(null));
    dispatch(setStoredDiscountType(null));
    dispatch(setStoredPromoCreditAmount(null));
    dispatch(resetBookingPricing());
    navigate(ROUTE_ACCOUNT_ROOT);
  };

  let accountNote: JSX.Element | null = null;
  if (!isFranchiseRole && contactEmail) {
    if (existingAccount === true && (!loggedIn || loggedInHere)) {
      accountNote = (
        <ExistingAccountPrompt
          email={contactEmail}
          onLoggedIn={() => setLoggedInHere(true)}
        />
      );
    } else if (!loggedIn && existingAccount !== false) {
      accountNote = <CreateAccountPrompt email={contactEmail} />;
    }
  }

  // The consumer skin's "You're booked." (consumer/ConsumerConfirmation);
  // the purchase and Booking Completed events above fire either way.
  if (aperio && skin === 'consumer') {
    return (
      <ConsumerConfirmation
        workOrderId={workOrderId}
        email={contactEmail}
        prepaid={prepaymentState === 'PAID'}
        prepaidAmountCents={prepaidAmountCents}
        onPayNow={isPrepay && prepaymentToken && !isFranchiseRole
          && !(payerSelected && payerRequest)
          ? () => navigate(ROUTE_BOOK_PAYMENT)
          : undefined}
        payerNote={isPrepay && payerSelected && payerRequest
          && !isFranchiseRole ? (
            <PayerPaymentNote
              request={payerRequest}
              token={prepaymentToken}
              bookerEmail={contactEmail}
              prepayDiscountPercent={prepayDiscountPercent}
              onPayNow={prepaymentToken
                ? () => navigate(ROUTE_BOOK_PAYMENT)
                : undefined}
            />
          ) : undefined}
        accountNote={accountNote}
        calendarLinks={calendarLinks}
        walletSaveUrl={walletSaveUrl}
      />
    );
  }

  if (aperio) {
    const vehicleLabel = [car?.year, car?.make, car?.model]
      .filter(Boolean).join(' ');
    // The marketing site's home in the language on screen (/fr, /es).
    const lang = portalLanguage();
    const siteBase = marketingBase(tenant?.marketingSiteUrl);
    const langPath = MARKETING_LANGUAGES.find((l) => l.code === lang)?.path
      || '';
    return (
      <AperioConfirmation
        confirmed={!!potentialVanId}
        outsideArea={!potentialFranchiseId}
        staff={!!isFranchiseRole}
        workOrderId={workOrderId}
        name={name}
        email={contactEmail}
        street={serviceAddress?.street}
        city={serviceAddress?.city}
        provinceCode={serviceAddress?.provinceCode}
        postalCode={serviceAddress?.postalCode}
        dateLabel={dateTime
          ? formatDate(parseISO(dateTime.date), 'EEEE, MMMM d, yyyy', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
          : null}
        timeLabel={appointmentTime}
        vehicleLabel={vehicleLabel}
        isPrepay={isPrepay}
        prepaid={prepaymentState === 'PAID'}
        prepaidAmountCents={prepaidAmountCents}
        prepayDiscountPercent={prepayDiscountPercent}
        onPayNow={isPrepay && prepaymentToken && !isFranchiseRole
          ? () => navigate(ROUTE_BOOK_PAYMENT)
          : undefined}
        payerNote={isPrepay && payerSelected && payerRequest
          && !isFranchiseRole ? (
            <PayerPaymentNote
              request={payerRequest}
              token={prepaymentToken}
              bookerEmail={contactEmail}
              prepayDiscountPercent={prepayDiscountPercent}
              onPayNow={prepaymentToken
                ? () => navigate(ROUTE_BOOK_PAYMENT)
                : undefined}
            />
          ) : undefined}
        loggedIn={loggedIn}
        onBookAnother={newAppointment}
        onGoToAccount={goToAccount}
        homeUrl={siteBase ? `${siteBase}${langPath}` || null : null}
        onGoHome={goHome}
        phone={brand?.phone ?? '+1-833-466-4520'}
        phoneHuman={lang === 'fr'
          ? marketingFooterCopy(lang).phoneHuman
          : brand?.phoneHuman ?? '1-833-466-4520'}
        helloEmail={helloEmail}
        accountNote={accountNote}
        calendarLinks={calendarLinks}
        walletSaveUrl={walletSaveUrl}
      />
    );
  }

  return (
    <PageMain className={styles.success}>
      {(potentialFranchiseId) && (
        <div className={styles.header}>
          <h1>
            {potentialVanId
              ? 'You\'re booked!'
              : 'Booking request received'}
          </h1>
        </div>
      )}
      {(!potentialFranchiseId) && (
        <div className={styles.header}>
          <h1>Outside Service Area</h1>
        </div>
      )}
      <div className={styles.body}>
        {(potentialFranchiseId) && (
          <div className={styles.bookingInfoSection}>
            {isFranchiseRole ? (
              <>
                <h4>
                  {potentialVanId
                    ? 'Service confirmed'
                    : 'Service requested'}
                  {customer.name && (
                  <>
                    {' for '}
                    {customer.name}
                  </>
                  )}
                </h4>
                <p>
                  {customer.name ? (
                    <>
                      {customer.name}
                      &apos;s service is scheduled for
                    </>
                  ) : (
                    <>Service is scheduled for</>
                  )}
                  <span className={styles.date}>
                    {' '}
                    {appointmentDate}
                    {' '}
                    at
                    {' '}
                    {appointmentTime}
                  </span>
                  .
                  A confirmation email has been sent to the provided email
                  address. If the customer has an account with
                  {' '}
                  {brandName}
                  , the
                  booking will be added to their account.
                  <br />
                  <br />
                  You can reschedule or cancel your appointment through
                  the appointment details section in your account.
                  Alternatively, feel free to reach out to us via phone
                  {' '}
                  <a href="tel:1-833-466-4520">1-833-466-4520</a>
                  {' '}
                  or email
                  {' '}
                  <a href={`mailto:${helloEmail}`}>{helloEmail}</a>
                </p>

              </>
            ) : (
              <>
                <h4>
                  Thank you,
                  {' '}
                  {name}
                  !
                </h4>
                <p>
                  {potentialVanId ? (
                    <>
                      Your appointment is locked in for
                      {' '}
                      <span className={styles.date}>
                        {appointmentDate}
                        {' '}
                        at
                        {' '}
                        {appointmentTime}
                      </span>
                      . A confirmation email is on its way with everything
                      you need.
                    </>
                  ) : (
                    <>
                      We have your request for
                      {' '}
                      <span className={styles.date}>
                        {appointmentDate}
                        {' '}
                        at
                        {' '}
                        {appointmentTime}
                      </span>
                      . Your local team will confirm the time shortly.
                    </>
                  )}
                  <br />
                  <br />
                  You can reschedule or cancel your appointment through
                  the appointment details section in your account.
                  Alternatively, feel free to reach out to us via phone
                  {' '}
                  <a href="tel:1-833-466-4520">1-833-466-4520</a>
                  {' '}
                  or email
                  {' '}
                  <a href={`mailto:${helloEmail}`}>{helloEmail}</a>
                </p>
                {potentialVanId && calendarLinks && (
                  <AddToCalendar
                    links={calendarLinks}
                    headingLevel="h4"
                    headingClassName={styles.subheader}
                  />
                )}

              </>
            )}
            <h4 className={styles.subheader}>Requested Service</h4>
            <Invoice
              className={styles.invoice}
              showDisclaimer={false}
              condense
            />
            {isPrepay && !isFranchiseRole && (
              <div
                className={cx(styles.paymentStatus, {
                  [styles.paid]: prepaymentState === 'PAID',
                })}
              >
                {prepaymentState === 'PAID' ? (
                  <p>
                    <span className="label">
                      Paid
                      {prepaidAmountCents > 0
                        ? ` ${formatCents(prepaidAmountCents)}`
                        : ''}
                      .&nbsp;
                    </span>
                    Your
                    {' '}
                    {prepayDiscountPercent}
                    % prepay discount is locked in and nothing is due on the
                    day of service. A receipt is on its way to your inbox.
                  </p>
                ) : (
                  <>
                    <p>
                      <span className="label">
                        Save
                        {' '}
                        {prepayDiscountPercent}
                        % by paying now.&nbsp;
                      </span>
                      Your booking is saved either way. Pay now to keep the
                      prepay discount, or pay the regular price after your
                      service.
                    </p>
                    {prepaymentToken && (
                      <Button
                        onClick={() => navigate(ROUTE_BOOK_PAYMENT)}
                      >
                        {`Pay now and save ${prepayDiscountPercent}%`}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            {accountNote}
            {isFranchiseRole ? (
              <>
                <p>
                  <span className="label">
                    No payment was required to book.
                  </span>
                  {' '}
                  {isPrepay
                    ? `The customer chose to prepay and save ${prepayDiscountPercent}%. `
                      + 'They receive a payment link by email; if they pay, '
                      + 'the amount is applied to the invoice at completion.'
                    : 'Payment can be made by card or using the '
                      + '"Pay Later" option on the day of service.'}
                </p>
                <p>
                  <span>
                    The quote is subject to change if vehicle
                    information is not provided in full.
                  </span>
                </p>
              </>

            ) : (
              <>
                <p>
                  <span className="label">
                    No payment was required to book.
                  </span>
                  {' '}
                  {isPrepay && prepaymentState === 'PAID'
                    && 'Your prepayment covers the quoted total. If the final '
                      + 'bill differs, only the difference is charged to or '
                      + 'refunded to the same card.'}
                  {isPrepay && prepaymentState !== 'PAID'
                    && `Pay now to keep your ${prepayDiscountPercent}% prepay `
                      + 'discount, or pay the regular price after your service.'}
                  {!isPrepay
                    && 'Payment can be made by card or using the '
                      + '"Pay Later" option on the day of service.'}
                </p>
                <p>
                  <span>
                    <span className="label">
                      Your total cost may be higher.&nbsp;
                    </span>
                    Without full vehicle information,
                    we are only able to provide a baseline quote.
                    <br />
                    <span>
                      Tax is not included in the price.
                    </span>
                  </span>
                </p>

              </>
            )}
          </div>
        )}
        {(!potentialFranchiseId) && (
          <div className={styles.bookingInfoSection}>
            {isFranchiseRole ? (
              <>
                <h4>
                  Service requested for
                  {' '}
                  {customer.name}
                </h4>
                <p>
                  {customer.name}
                  &apos;s
                  {' '}
                  service is scheduled for
                  <span className={styles.date}>
                    {' '}
                    {appointmentDate}
                    {' '}
                    at
                    {' '}
                    {appointmentTime}
                  </span>
                  .
                  It appears that the booking could not be completed because the
                  location is outside of the service area.
                  Please log into the admin panel or contact
                  {' '}
                  {brandName}
                  {' '}
                  head office for assistance with this issue.
                  <br />
                  <br />
                  Feel free to reach out to us via phone
                  {' '}
                  <a href="tel:1-833-466-4520">1-833-466-4520</a>
                  {' '}
                  or email
                  {' '}
                  <a href={`mailto:${helloEmail}`}>{supportEmail}</a>
                </p>

              </>
            ) : (
              <>
                <h4>
                  Thank you,
                  {' '}
                  {name}
                  !
                </h4>
                <p>
                  We have received your booking request for
                  {' '}
                  <span className={styles.date}>
                    {appointmentDate}
                    {' '}
                    at
                    {' '}
                    {appointmentTime}
                  </span>
                  .
                  Unfortunately, it appears that your location
                  is outside of our service area.
                  However, we are expanding fast and look
                  forward to servicing you soon!
                  <br />
                  Please feel free to reach out if you have any questions.
                  {' '}
                  <a href="tel:1-833-466-4520">1-833-466-4520</a>
                  {' '}
                  or
                  {' '}
                  <a href={`mailto:${helloEmail}`}>{helloEmail}</a>
                  {' '}
                  for further assistance.
                </p>

              </>
            )}
            <h4 className={styles.subheader}>Requested Service</h4>
            <Invoice
              className={styles.invoice}
              showDisclaimer={false}
              condense
            />
            {isFranchiseRole ? (
              <>
                <p>
                  <span className="label">
                    No payment was required to book.
                  </span>
                  {' '}
                  {isPrepay
                    ? `The customer chose to prepay and save ${prepayDiscountPercent}%. `
                      + 'They receive a payment link by email; if they pay, '
                      + 'the amount is applied to the invoice at completion.'
                    : 'Payment can be made by card or using the '
                      + '"Pay Later" option on the day of service.'}
                </p>
                <p>
                  <span>
                    The quote is subject to change if vehicle
                    information is not provided in full.
                  </span>
                </p>
              </>

            ) : (
              <>
                <p>
                  <span className="label">
                    No payment was required to book.
                  </span>
                  {' '}
                  {isPrepay && prepaymentState === 'PAID'
                    && 'Your prepayment covers the quoted total. If the final '
                      + 'bill differs, only the difference is charged to or '
                      + 'refunded to the same card.'}
                  {isPrepay && prepaymentState !== 'PAID'
                    && `Pay now to keep your ${prepayDiscountPercent}% prepay `
                      + 'discount, or pay the regular price after your service.'}
                  {!isPrepay
                    && 'Payment can be made by card or using the '
                      + '"Pay Later" option on the day of service.'}
                </p>
                <p>
                  <span>
                    <span className="label">
                      Your total cost may be higher.&nbsp;
                    </span>
                    Without full vehicle information,
                    we are only able to provide a baseline quote.
                    <br />
                    <span>
                      Tax is not included in the price.
                    </span>
                  </span>
                </p>

              </>
            )}
          </div>
        )}

        <div className={styles.mapSection}>
          <img
            src={currentImageSrc}
            alt="google maps of your address"
            className={styles.map}
          />
          <div className={styles.secretDiv} ref={anchorEl} />
          {!mapLoading && (
            <Popper
              open={open}
              anchorEl={anchor}
              placement="top"
              modifiers={[
                {
                  name: 'flip',
                  enabled: true,
                  options: {
                    altBoundary: true,
                    rootBoundary: 'document',
                    padding: 8,
                    marginBottom: '1rem',
                  },
                },
                {
                  name: 'preventOverflow',
                  enabled: false,
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: true,
                    rootBoundary: 'document',
                    padding: 8,
                    marginBottom: '1rem',
                  },
                },
              ]}
            >
              <div className={styles.popper}>
                <p className={styles.popperHeader}>
                  {brandName}
                  {' '}
                  Booking
                </p>
                <div className={styles.popperSection}>
                  <Location className={styles.icon} />
                  <p>{serviceAddress?.street}</p>
                </div>
                <div className={styles.popperSection}>
                  <Calendar className={styles.icon} />
                  <p>{smallAppointmentDate}</p>
                </div>
                <div className={styles.popperSection}>
                  <Clock className={styles.icon} />
                  <p>{smallAppointmentTime}</p>
                </div>
              </div>
            </Popper>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        { loggedIn ? (
          <>
            <Button
              onClick={goHome}
              variant="tertiary"
            >
              Book Another Appointment
            </Button>
            <Button
              onClick={goToAccount}
              variant="primary"
            >
              My Account
            </Button>
          </>
        ) : (
          <Button
            onClick={newAppointment}
            variant="primary"
          >
            New Appointment
          </Button>
        )}
      </div>
    </PageMain>
  );
}
